import { TaskResponse } from "@9amhealth/openapi";
import { useBloc } from "@blac/react";
import styled from "@emotion/styled";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  IconCarouselChevronLeft,
  IconCarouselChevronRight
} from "src/constants/icons";
import { APP_BREAKPOINT_SM } from "src/constants/layout";
import AppointmentsBloc from "src/state/AppointmentsBloc/AppointmentsBloc";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import AppointmentCard from "src/ui/components/AppointmentsList/components/AppointmentCard";
import Translate from "src/ui/components/Translate/Translate";
import CollectionTitle from "../StyledComponents/CollectionTitle";
import AppointmentTaskCard from "./components/AppointmentTaskCard";
import Carousel from "./components/Carousel/Carousel";
import {
  CarouselButton,
  CarouselButtonWrapper
} from "./components/Carousel/CarouselButton";
import { CarouselCubit } from "./components/Carousel/CarouselCubit";
import CarouselItem from "./components/Carousel/CarouselItem";
import { OpacityGradient } from "./components/Carousel/OpacityGradient";
import Pagination from "./components/Carousel/Pagination";

const Wrapper = styled.div`
  margin: 1.5rem 0 4rem;

  @media screen and (max-width: ${APP_BREAKPOINT_SM}px) {
    margin-top: 0.5rem;
  }

  position: relative;
`;

const AppointmentsList: FC = () => {
  const [{ scheduledAppointments }] = useBloc(AppointmentsBloc);
  const [{ locked }, { handleScrollTo, itemPrefix }] = useBloc(CarouselCubit);
  const [{ tasks }, { loadProgramTasks }] = useBloc(TaskManagementBloc);

  const [loading, setLoading] = useState(false);

  const initialLabTask = useMemo(
    () =>
      tasks.find(
        (task) =>
          task.slug === TaskKey.COMPLETE_INITIAL_LAB_ORDER &&
          (task.status === TaskResponse.status.AVAILABLE ||
            task.status === TaskResponse.status.IN_PROGRESS) &&
          task.additionalData?.requisitionFileId
      ),
    [tasks]
  );

  const recurringLabTask = useMemo(
    () =>
      tasks.find(
        (task) =>
          task.slug === TaskKey.COMPLETE_RECURRING_LAB_ORDER &&
          task.status === TaskResponse.status.AVAILABLE &&
          task.additionalData?.requisitionFileId
      ),
    [tasks]
  );

  const appointmentTask = initialLabTask ?? recurringLabTask;

  useEffect(() => {
    let mounted = true;
    if (!appointmentTask) {
      setLoading(true);
      void loadProgramTasks(KnownProgram.CARE).finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    }
    return () => {
      mounted = false;
    };
  }, []);

  const elementsCount =
    scheduledAppointments.length + Number(!!appointmentTask);
  const isPaginationHidden = elementsCount <= 1;

  if (!elementsCount || loading) {
    return null;
  }

  return (
    <Wrapper>
      <CollectionTitle style={{ marginBottom: "0.6rem" }}>
        <Translate msg="appointment.list.title" />
      </CollectionTitle>

      <Carousel isPaginationHidden={isPaginationHidden}>
        <OpacityGradient elementsCount={elementsCount} />
        {appointmentTask && (
          <>
            <CarouselItem id={itemPrefix} zIndex={scheduledAppointments.length}>
              <AppointmentTaskCard appointmentTask={appointmentTask} />
            </CarouselItem>
          </>
        )}
        {scheduledAppointments.map((appointment, index) => (
          <CarouselItem
            id={`${itemPrefix}-${index - 1}`}
            key={appointment.id}
            // previous items should have higher z-index to fix clipping shadow issue
            zIndex={scheduledAppointments.length - index - 1}
          >
            <AppointmentCard appointment={appointment} />
          </CarouselItem>
        ))}
        <OpacityGradient elementsCount={elementsCount} />
      </Carousel>

      {!isPaginationHidden && (
        <>
          <Pagination elementsCount={elementsCount} />

          <CarouselButtonWrapper>
            <CarouselButton
              disabled={locked}
              onClick={() => handleScrollTo("prev")}
            >
              <IconCarouselChevronLeft />
            </CarouselButton>

            <CarouselButton
              disabled={locked}
              onClick={() => handleScrollTo("next")}
            >
              <IconCarouselChevronRight />
            </CarouselButton>
          </CarouselButtonWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default AppointmentsList;
